import { useMemo, useEffect, useState, useCallback } from 'react';
import { RoomEvent, MatrixEventEvent, NotificationCountType } from '../constants/matrixEvents';
import logger from '../utils/logger';
import { getEffectiveReadMarkerEventId } from '../utils/unreadCountUtils';
import { useManualUnreadCount } from './useManualUnreadCount';

/**
 * Hook for tracking unread messages in a Matrix room
 *
 * This hook calculates the number of unread messages based on the read marker position
 * and listens for Matrix events that might change the unread count.
 *
 * IMPLEMENTATION NOTE: Now uses manual client-side counting (Netsfere approach)
 * in addition to SDK count for validation and fallback.
 *
 * @param {object} params - Hook parameters
 * @param {object} params.room - Matrix room object
 * @param {object} params.client - Matrix client object
 * @param {Array} params.messageGroups - Array of message groups from useRoom
 * @returns {object} Unread tracking state
 */
export const useUnreadTracking = ({ room, client, messageGroups }) => {
  // State to trigger recalculation when events fire
  const [, setUpdateTrigger] = useState(0);
  const forceUpdate = useCallback(() => {
    setUpdateTrigger((prev) => prev + 1);
  }, []);

  // Get manual unread count (Netsfere approach with hybrid data source)
  const manualResult = useManualUnreadCount(room, client, messageGroups);

  const unreadTracking = useMemo(() => {
    // Handle edge case: null room or client (expected during initialization)
    if (!room || !client) {
      logger.debug('[useUnreadTracking] Room or client is null during initialization, returning safe defaults');
      return {
        unreadCount: 0,
        manualUnreadCount: 0,
        sdkUnreadCount: 0,
        firstUnreadEventId: null,
        hasUnread: false,
      };
    }

    // Get official unread count from Matrix SDK (server-synced)
    const sdkUnreadCount = room.getUnreadNotificationCount(NotificationCountType.Total);

    const myUserId = client.getUserId();
    const readMarkerId = getEffectiveReadMarkerEventId(room, myUserId);

    // Extract manual count from result object
    const manualUnreadCount = manualResult.count;

    logger.debug(
      `[useUnreadTracking] Manual count: ${manualUnreadCount} (source: ${manualResult.source}, reliable: ${manualResult.isReliable}), ` +
      `SDK count: ${sdkUnreadCount}, readMarkerId: ${readMarkerId}`,
    );

    // Log if using fallback
    if (!manualResult.isReliable) {
      logger.debug(`[useUnreadTracking] Using ${manualResult.source}: ${manualResult.reason}`);
    }

    // Use manual count directly (fallback already handled in useManualUnreadCount)
    const unreadCount = manualUnreadCount;
    // If no unread messages, return early
    if (unreadCount === 0) {
      return {
        unreadCount: 0,
        manualUnreadCount,
        sdkUnreadCount,
        firstUnreadEventId: null,
        hasUnread: false,
      };
    }

    // Find firstUnreadEventId by locating read marker in timeline
    if (!messageGroups || messageGroups.length === 0) {
      logger.debug('[useUnreadTracking] No messageGroups, returning count only');
      return {
        unreadCount,
        manualUnreadCount,
        sdkUnreadCount,
        firstUnreadEventId: null,
        hasUnread: unreadCount > 0,
      };
    }

    // Flatten message groups to find marker position
    const allEvents = messageGroups.reduce((acc, group) => {
      if (group.messages && Array.isArray(group.messages)) {
        const validMessages = group.messages.filter((msg) => typeof msg?.getId === 'function');
        return acc.concat(validMessages);
      }
      return acc;
    }, []);

    if (allEvents.length === 0) {
      logger.debug('[useUnreadTracking] No valid events, returning count only');
      return {
        unreadCount,
        manualUnreadCount,
        sdkUnreadCount,
        firstUnreadEventId: null,
        hasUnread: unreadCount > 0,
      };
    }

    // Find marker position in timeline
    let markerIndex = -1;
    if (readMarkerId) {
      markerIndex = allEvents.findIndex((event) => event.getId() === readMarkerId);
    }

    // Determine first unread event
    let firstUnreadEventId = null;
    if (markerIndex === -1) {
      // Marker not found - first message is potentially unread
      firstUnreadEventId = allEvents[0].getId();
    } else if (markerIndex < allEvents.length - 1) {
      // First unread is message after marker
      firstUnreadEventId = allEvents[markerIndex + 1].getId();
    }

    logger.debug(`[useUnreadTracking] firstUnreadEventId: ${firstUnreadEventId}, markerIndex: ${markerIndex}`);

    return {
      unreadCount, // Primary: manual count, fallback: SDK
      manualUnreadCount, // Client-side calculated count
      sdkUnreadCount, // Server-synced SDK count
      firstUnreadEventId, // Calculated from timeline
      hasUnread: unreadCount > 0,
    };
  }, [room, client, messageGroups, manualResult]);

  // Event handlers that trigger recalculation
  const handleTimelineEvent = useCallback((event) => {
    if (!room) return;
    logger.debug('[useUnreadTracking] Timeline event received, recalculating unread count');
    forceUpdate();
  }, [forceUpdate, room]);

  const handleUnreadNotifications = useCallback(() => {
    if (!room) return;
    logger.debug('[useUnreadTracking] Unread notifications changed, recalculating unread count');
    forceUpdate();
  }, [forceUpdate, room]);

  const handleAccountDataEvent = useCallback((event) => {
    if (!room) return;
    logger.debug('[useUnreadTracking] AccountData event received, recalculating unread count');
    forceUpdate();
  }, [forceUpdate, room]);

  const handleDecryptedEvent = useCallback((event) => {
    // Only trigger for events in this room
    if (room && event.getRoomId() === room.roomId) {
      logger.debug('[useUnreadTracking] Event decrypted in room, recalculating unread count');
      forceUpdate();
    }
  }, [room, forceUpdate]);

  // Register event listeners for Matrix events that affect unread count
  useEffect(() => {
    if (!room || !client) return;

    // Register room-level listeners
    room.on(RoomEvent.Timeline, handleTimelineEvent);
    room.on(RoomEvent.UnreadNotifications, handleUnreadNotifications);
    room.on(RoomEvent.AccountData, handleAccountDataEvent);
    client.on(MatrixEventEvent.Decrypted, handleDecryptedEvent);

    logger.debug('[useUnreadTracking] Registered room and client event listeners');

    // Cleanup function to remove listeners
    return () => {
      room.removeListener(RoomEvent.Timeline, handleTimelineEvent);
      room.removeListener(RoomEvent.UnreadNotifications, handleUnreadNotifications);
      room.removeListener(RoomEvent.AccountData, handleAccountDataEvent);
      client.removeListener(MatrixEventEvent.Decrypted, handleDecryptedEvent);
      logger.debug('[useUnreadTracking] Removed room and client event listeners');
    };
  }, [room, client, handleTimelineEvent, handleUnreadNotifications, handleAccountDataEvent, handleDecryptedEvent]);

  return unreadTracking;
};
